import * as lodash from "lodash";
import moment from "moment";

import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LookupDictionary } from "@/Common/Models/LookupDictionary";
import { Comment } from "@/Common/Models/Patient/Comment";
import { Patient } from "@/Common/Models/Patient/Patient";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { toLookupDictionary } from "@/Common/Utilities/Collections/ArrayUtility";
import { CumulativePatientProfileModel } from "@/Patient/Api/Dto/Responses/CumulativePatientProfileModel";
import { PendingItemsModel } from "@/Patient/Components/MedicalSummaryPanel/Section/PendingItemsModel";
import { MedicalNoteIndexHelper } from "@/Patient/Helpers/MedicalNoteIndexHelper";
import { MedicalNotesConfigurationHelper } from "@/Patient/Helpers/MedicalNotesConfigurationHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { DiagnosisModel } from "@/Patient/Models/MedicalSummary/DiagnosisModel";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { FavoriteDiagnosisComposite } from "@/Patient/Models/MedicalSummary/FavoriteDiagnosisComposite";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { ValidationNote } from "@/Patient/Models/MedicalSummary/ValidationNote";
import { MedicationLegalStatusHelper } from "@/Patient/Models/Medication/Enums/MedicationLegalStatus";
import { GroupedMedicationModel, MedicationDosageDisplayMode, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PatientPrescriptionItem } from "@/Patient/Models/Medication/PatientPrescriptionItem";
import { MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IMedicalItemsModel } from "@/Patient/Models/UI/MedicalSummary/IMedicalItemsModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { PatientMedicalItemsHelper } from "./PatientMedicalItemsHelper";
import { PatientModelHelper } from "./PatientModelHelper";

/**
 * The mode for rebuilding the encounter search index.
 */
enum EncounterSearchIndexRebuildMode {
    SkipRebuild,
    RebuildOnEncounterListChange,
    ForceRebuild
}

class PatientMedicalSummaryHelper {
    public static concatAllProblemTypes(summaryModel: IPatientMedicalSummaryModel): MedicalItemProblemAndCondition[] {
        let allMedicalProblems: MedicalItemProblemAndCondition[] = [];

        allMedicalProblems = allMedicalProblems.concat(summaryModel.get("MedicalItemsProblemsOnGoing").toArray());
        allMedicalProblems = allMedicalProblems.concat(summaryModel.get("MedicalItemsProblemsHistory").toArray());
        allMedicalProblems = allMedicalProblems.concat(summaryModel.get("MedicalItemsSurgeryHistory").toArray());
        allMedicalProblems = allMedicalProblems.concat(summaryModel.get("MedicalItemsPsychiatricHistory").toArray());
        allMedicalProblems = allMedicalProblems.concat(summaryModel.get("MedicalItemsGynecologicalHistory").toArray());

        return allMedicalProblems;
    }

    public static getEncounterDiagnosesForNoteTile(encounterId: string, summaryModel: IPatientMedicalSummaryModel): string[] {
        const showDiagnosisCode = settingsStore.configuration.get("ShowDiagnosisCode") === true;

        const problemsAndDiagnosis = PatientMedicalSummaryHelper.concatAllProblemTypes(summaryModel);
        const qualifiedDetailsStatusesToBeShown = [
            "DSConfirmed",
            "DSPossible",
            "DSTentative",
            "DSNotSpecified"
        ];
        const qualifiedDetailsStatusesLookupIds = cacheStore
            .FindLookupsByTypeCode("DiagnosisStatus")
            .filter(lookup => qualifiedDetailsStatusesToBeShown.contains(lookup.LookupCode))
            .map(lookup => lookup.LookupID);

        const diagnoses = problemsAndDiagnosis
            .filter(p => p.RelatedEncounterId === encounterId &&
                p.QualifiedDetails?.any() &&
                !p.EMRTemplateIsInvalid &&
                p.EMRTemplateMedicalItemType === MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions &&
                p.EMRTemplateMedicalItemSubType === MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing
            )
            .sort((a, b) => new Date(a.CreationDate).getTime() - new Date(b.CreationDate).getTime())
            .map(item => item.QualifiedDetails
                .filter(qualifiedDetail =>
                    qualifiedDetailsStatusesLookupIds.contains(qualifiedDetail.StatusLookupId)
                    || !qualifiedDetail.StatusLookupId
                )
                .map(qualifiedDetail => `${showDiagnosisCode && qualifiedDetail.Code ? qualifiedDetail.Code + " - " : ""}${qualifiedDetail.Text}`)
            )
            .flatMap(item => item);

        return diagnoses;
    }

    public static ReorderGroupedMedicalItems(medicalItems: MedicalItem[], itemId: string, newItemIndex: number): MedicalItem[] {
        if (medicalItems) {
            // group medical items by emr ID
            var groupedItems: MedicalItem[][] = ArrayHelper
                .GroupBy(
                    medicalItems,
                    (item: MedicalItem) => item.EMRTemplateId
                );

            // get old index in groupedItems
            var oldIndex = -1;
            for (var i = 0; i < groupedItems.length; i++) {
                if (groupedItems[i][0]) {
                    if (groupedItems[i][0].EMRTemplateId == itemId) {
                        oldIndex = i;
                        break;
                    }
                }
            }

            // get new index in groupedItems
            if (oldIndex > -1) {
                var groupToMove = groupedItems[oldIndex];

                var positionChange = newItemIndex - oldIndex;
                var newIndex = (oldIndex + positionChange);

                if (newIndex < 0) newIndex = 0;

                if (newIndex > groupedItems.length) newIndex = groupedItems.length;

                groupedItems.splice(oldIndex, 1);
                groupedItems.splice(newIndex, 0, groupToMove);
            }

            // apply new EMRSortIndex to all subitems of groups and ungroup medical items
            medicalItems = [];
            for (var i = 0; i < groupedItems.length; i++) {
                groupedItems[i].forEach(x => {
                    x.EMRTemplateSortIndex = i;
                    medicalItems.push(x);
                });
            }
        }

        return medicalItems;
    }

    public static ReorderMedicalItems(medicalItems: MedicalItem[], itemId: string, newItemIndex: number, idPropName: string = "Id"): MedicalItem[] {
        if (!medicalItems?.length)
            return [];

        if (newItemIndex < 0)
            throw new Error("newItemIndex must be a non-negative number.");
        if (!itemId)
            throw new Error("itemId must be provided.");

        const visibleAlerts = PatientMedicalSummaryHelper.FilterItemsToDisplayInCPP(medicalItems.toImmutable()) as MedicalItemAlertsAndSpecialNeeds[];
        const newItemSortIndex = visibleAlerts[newItemIndex].EMRTemplateSortIndex;
        const oldIndex = medicalItems.indexOfObject(idPropName, itemId);
        const itemEMRId = medicalItems[oldIndex].EMRTemplateId;

        medicalItems = medicalItems.move(oldIndex, newItemSortIndex);

        // ensure all items of same EMRTemplateID have same order...
        medicalItems = medicalItems.map((medicalItem, index): MedicalItem => {
            medicalItem.EMRTemplateSortIndex = index;
            if (medicalItem.EMRTemplateId == itemEMRId)
                medicalItem.EMRTemplateSortIndex = newItemSortIndex;

            return medicalItem;
        });

        return medicalItems;
    }

    /**
     * Search all encounter based on searchString, searchDateFrom, or searchDateTo, and set encounters' search index if an encounter is not evaluated yet and tryRebuildSearchIndex is true
     * @param medicalSummaryModel
     * @param searchIndexRebuildMode
     * @param patientDateOfBirth
     * @returns
     */
    public static FilterEncountersClientSide(medicalSummaryModel: IPatientMedicalSummaryModel, searchIndexRebuildMode: EncounterSearchIndexRebuildMode): IPatientMedicalSummaryModel {
        const clinicalVisitLookupId = cacheStore.FindLookupByLookupCode("Clinic Visite", "CONSULTATIONCONTEXT").LookupID;

        const searchString = medicalSummaryModel.get("EncounterSearchTerm");
        const searchDateFrom = medicalSummaryModel.get("EncounterSearchDateFrom");
        const searchDateTo = medicalSummaryModel.get("EncounterSearchDateTo");
        const displayOnlyMyNotes = medicalSummaryModel.get("EncounterFilterMyNotes");
        const allEncounters = medicalSummaryModel.get("Encounters").toArray();
        let encountersSearchStrings = medicalSummaryModel.get("EncountersSearchStrings");

        const displayOnlyUnsigned =
            !cacheStore.ClinicSettings.EnableDirectPatientProfileEdits
            && medicalSummaryModel.get("IsDisplayingOnlyUnsigned");

        const displayOnlyClinicVisitNotes =
            cacheStore.ClinicSettings.EnableDirectPatientProfileEdits
            && medicalSummaryModel.get("IsDisplayOnlyClinicVisitNotes");

        const encounterContexts =
            cacheStore.ClinicSettings.EnableDirectPatientProfileEdits
            && MedicalNotesConfigurationHelper.getSpecificConfigAndUpdateNotesFilterSettings()?.EncounterContexts;

        if (searchIndexRebuildMode === EncounterSearchIndexRebuildMode.ForceRebuild
            || (
                searchIndexRebuildMode === EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange
                && allEncounters && encountersSearchStrings && allEncounters.length !== Object.keys(encountersSearchStrings).length
            )
        ) {
            medicalSummaryModel = PatientMedicalSummaryHelper.RebuildSearchIndex(medicalSummaryModel);
            encountersSearchStrings = medicalSummaryModel.get("EncountersSearchStrings");
        }

        const helper = new MyleHelper();

        const filteredEncounters = allEncounters.filter(enc => {
            const invalid =
                (displayOnlyUnsigned && enc.SignedOffDate)
                || (searchString && encountersSearchStrings?.[enc.Id] && helper.RemoveDiacritics(encountersSearchStrings[enc.Id]).toUpperCase().indexOf(helper.RemoveDiacritics(searchString).toUpperCase()) === -1)
                || (searchDateFrom && moment(enc.ConsultationDate) < moment(searchDateFrom))
                || (searchDateTo && moment(enc.ConsultationDate) > moment(searchDateTo))
                || (displayOnlyMyNotes && !enc.EncounterRelatedEmployees.some(p => p.EmployeeId == sessionStore.getCurrentEmployee().Model.ContactFacet_id))
                || (encounterContexts && !encounterContexts.contains(enc.ConsultationContextLookupId))
                || (displayOnlyClinicVisitNotes && enc.ConsultationContextLookupId !== clinicalVisitLookupId);
            return !invalid;
        });

        medicalSummaryModel = medicalSummaryModel.set("DisplayedEncounters", Immutable.List(filteredEncounters));

        return medicalSummaryModel;
    }

    public static ReApplySortingAndNumbering(medicalItems: MedicalItem[]): MedicalItem[] {
        medicalItems.forEach(x => {
            if (x.EMRTemplateIsVisibleInCPP != true || x.EMRTemplateIsInvalid == true)
                x.EMRTemplateSortIndex = 99999;
        });
        return medicalItems.sortByProperty(x => x.EMRTemplateSortIndex);
    }

    public static GetEMRTemplatesToDisplay(medicalItems: Immutable.List<MedicalItem>): MedicalItem[] {
        var emrTemplatesToRender: MedicalItem[] = [];

        if (medicalItems && medicalItems.count() > 0) {
            medicalItems.toArray().forEach((x: MedicalItem) => {
                var idx = emrTemplatesToRender.indexOfObject("EMRTemplateId", x.EMRTemplateId);
                if (idx < 0) {
                    emrTemplatesToRender.push(x);
                }
                else {
                    // keep the latest medicalitem for a given EMRTemplateId
                    if (moment(emrTemplatesToRender[idx].CommitDate) < moment(x.CommitDate)) {
                        emrTemplatesToRender[idx] = x;
                    }
                }
            });
        }

        return emrTemplatesToRender;
    }

    public static FilterItemsToDisplayInCPP(medicalItems: Immutable.List<MedicalItem>): MedicalItem[] {
        const emrTemplatesToRender: MedicalItem[] = [];

        if (medicalItems && medicalItems.count() > 0) {
            medicalItems.toArray().forEach((x: MedicalItem) => {
                if (x.EMRTemplateIsVisibleInCPP && x.EMRTemplateSortIndex != 99999 && x.EMRTemplateIsInvalid == false) {
                    var idx = emrTemplatesToRender.indexOfObject("EMRTemplateId", x.EMRTemplateId);
                    if (idx < 0) {
                        emrTemplatesToRender.push(x);
                    }
                    else {
                        // keep the latest medicalitem for a given EMRTemplateId
                        if (moment(emrTemplatesToRender[idx].CommitDate) < moment(x.CommitDate)) {
                            emrTemplatesToRender[idx] = x;
                        }
                    }
                }
            });
        }

        return emrTemplatesToRender;
    }

    // used to massage medical items to be displayed in a summary/encounter EMRTemplate detail view
    public static GetMedicalItemsToDisplay(medicalItems: Immutable.List<MedicalItem>, selectedEMRTemplateId: string, currentEncounterId: string = null): MedicalItem[] {
        let medicalItemsToDisplay: MedicalItem[] = [];

        if (medicalItems && medicalItems.count() > 0) {
            medicalItemsToDisplay = medicalItems.toArray().filter(item => item.EMRTemplateId == selectedEMRTemplateId) ?? [];

            if (medicalItemsToDisplay.any()) {
                medicalItemsToDisplay = MedicalItemModel.sortByCreationDateDesc(medicalItemsToDisplay);

                const latestMedicalItem = medicalItemsToDisplay.firstOrDefault();

                medicalItemsToDisplay.forEach(item => {
                    // make sure all medicalItems of same ID as last are also 'uncollapsed';
                    // By default, collapse the EMRTemplate that are not related to this note
                    if (
                        (currentEncounterId != null && item.RelatedEncounterId == currentEncounterId) ||
                        item.MedicalItemId === latestMedicalItem.MedicalItemId
                    )
                        item.IsCollapsed = false;
                    else
                        item.IsCollapsed = true;

                    return item;
                });
            }
        }

        return medicalItemsToDisplay;
    }

    public static SetMedicalDetailsModelInfo(model: IMedicalItemsModel, loadComments: boolean = true, navigationOrigin: MedicalItemViewOrigin, summaryModel: IPatientMedicalSummaryModel, encounterId: string = null, getEMRTemplateComments: any): IMedicalItemsModel {
        let selectedEMRTemplateId: string = null;

        if (model && model.get("AllMedicalItemsList") && model.get("AllMedicalItemsList").count() > 0) {
            const emrTemplatesToRender = PatientMedicalSummaryHelper.GetEMRTemplatesToDisplay(model.get("AllMedicalItemsList"));
            let medicalItemsToDisplay: MedicalItem[] = [];
            selectedEMRTemplateId = model.get("SelectedEMRTemplateId");

            if (emrTemplatesToRender && emrTemplatesToRender.length > 0 && (selectedEMRTemplateId == null || selectedEMRTemplateId == undefined || selectedEMRTemplateId == "")) {
                selectedEMRTemplateId = emrTemplatesToRender[0].EMRTemplateId;
                model = model.set("SelectedEMRTemplateId", selectedEMRTemplateId);
            }

            if (selectedEMRTemplateId)
                medicalItemsToDisplay = PatientMedicalSummaryHelper.GetMedicalItemsToDisplay(model.get("AllMedicalItemsList"), selectedEMRTemplateId, encounterId);

            model = model.set("EMRTemplateList", Immutable.List(emrTemplatesToRender));
            model = model.set("MedicalItemsList", Immutable.List(medicalItemsToDisplay));

            model = model.set("EMRTemplateComments", summaryModel.get("EMRTemplateComments"));
            model = model.set("EMRTemplateValidationNotes", summaryModel.get("EMRTemplateValidationNotes"));

            model = model.set("NavigationOrigin", navigationOrigin);
        }

        if (selectedEMRTemplateId && loadComments) {
            window.setTimeout(() => {
                getEMRTemplateComments(selectedEMRTemplateId);
            }, 0);
        }

        return model;
    }

    public static GetLifeStageString(medicalItem: MedicalItem, fromSummaryPanel: boolean = false): string {
        var lifestageString = "";

        // get age lifestage
        if (medicalItem.LifeStageMode && medicalItem.LifeStageMode == 1) {
            var age = medicalItem.LifeStageAge;
            if (age && age > 0) {
                lifestageString += age + " " + cacheStore.GetLocalizedValueByLookupId(medicalItem.LifeStageAgeStatusLookupId);
            }
        }
        else if (medicalItem.LifeStageStatusLookupId && medicalItem.LifeStageMode == 2) {
            var ageStage = cacheStore.GetLocalizedValueByLookupId(medicalItem.LifeStageStatusLookupId);
            lifestageString += ageStage;
        }
        else if (medicalItem.LifeStageDate) {
            // lifestageString += moment(medicalItem.LifeStageDate).format("YYYY-MM-DD");

            lifestageString += PatientMedicalItemsHelper.GetLifeStageDateString(medicalItem.LifeStageDate, medicalItem.LifeStagePrecisionDate, fromSummaryPanel);

            if (medicalItem.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.DayApprox && lifestageString != null && lifestageString.trim().length > 0)
                lifestageString = "≈ " + lifestageString;
        }
        else {
            lifestageString = "--";
        }

        return lifestageString;
    }

    public static GetAgeAtLifeStageOnset(medicalItem: MedicalItem, birthDate: Date): number {
        var result = -1;

        if (!(medicalItem.LifeStageMode == null) && medicalItem.LifeStageMode == LifeStageModeEnum.Age) {
            result = medicalItem.LifeStageAge;
        }
        else if (!(medicalItem.LifeStageMode == null) && medicalItem.LifeStageMode == LifeStageModeEnum.Date) {
            if (birthDate && medicalItem.LifeStageDate) {
                var result = moment(medicalItem.LifeStageDate).diff(birthDate, "years");
            }
            else
                result = 0;
        }
        else if (!(medicalItem.LifeStageMode == null) && medicalItem.LifeStageMode == LifeStageModeEnum.LifeStage) {
            if (!(medicalItem.LifeStageStatusLookupId == null)) {
                if (cacheStore.GetLookupById(medicalItem.LifeStageStatusLookupId).LookupCode == "NewBorn") result = 0;
                if (cacheStore.GetLookupById(medicalItem.LifeStageStatusLookupId).LookupCode == "Infant") result = 2;
                if (cacheStore.GetLookupById(medicalItem.LifeStageStatusLookupId).LookupCode == "Child") result = 15;
                if (cacheStore.GetLookupById(medicalItem.LifeStageStatusLookupId).LookupCode == "Adolescent") result = 17;
                if (cacheStore.GetLookupById(medicalItem.LifeStageStatusLookupId).LookupCode == "Adult") result = 18;
            }
        }

        return result;
    }

    public static GetDateDisplayAccordingToPrecision(date: Date, precision: LifeStageDatePrecisionEnum): string {
        var lifeStageString = "";

        if (date != null) {
            if (!(precision == null)) {
                if (precision == LifeStageDatePrecisionEnum.Year) {
                    lifeStageString = moment(date).year().toString();
                }
                else if (precision == LifeStageDatePrecisionEnum.MonthYear) {
                    lifeStageString = moment(date).format("YYYY-MM");
                }
                else if (precision == LifeStageDatePrecisionEnum.Day) {
                    lifeStageString = moment(date).format("YYYY-MM-DD");
                }
                else if (precision == LifeStageDatePrecisionEnum.DayApprox) {
                    lifeStageString = "~ " + moment(date).format("YYYY-MM-DD");
                }
            }
            else
                lifeStageString = moment(date).format("YYYY-MM-DD");
        }

        return lifeStageString;
    }

    public static GetAllMedicalProblemsFromSummary(summaryModel: IPatientMedicalSummaryModel): MedicalItemProblemAndCondition[] {
        var allItems: MedicalItemProblemAndCondition[] = [];

        allItems = allItems.concat(summaryModel.get("MedicalItemsProblemsOnGoing").toArray());
        allItems = allItems.concat(summaryModel.get("MedicalItemsProblemsHistory").toArray());
        allItems = allItems.concat(summaryModel.get("MedicalItemsSurgeryHistory").toArray());
        allItems = allItems.concat(summaryModel.get("MedicalItemsPsychiatricHistory").toArray());
        allItems = allItems.concat(summaryModel.get("MedicalItemsGynecologicalHistory").toArray());

        return allItems;
    }

    public static GetMedicalProblemHistoryForEncounter(summaryModel: IPatientMedicalSummaryModel, encounter: Encounter, subType: MedicalItemSubtypeEnum = null, patientBirthDate: Date): MedicalItemProblemAndCondition[] {
        let validationNotes = summaryModel.get("EMRTemplateValidationNotes");
        let allMedicalProblems: MedicalItemProblemAndCondition[] = PatientMedicalSummaryHelper.GetAllMedicalProblemsFromSummary(summaryModel);

        let allMedicalProblemsByEMR = toLookupDictionary(allMedicalProblems, x => x.EMRTemplateId);

        if (subType != null) {
            allMedicalProblemsByEMR = allMedicalProblemsByEMR.filter(
                x => x.EMRTemplateMedicalItemSubType == subType
                    || (
                        !x.EMRTemplateMedicalItemSubType
                        && subType == MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing
                    )
            );
        }

        // do not display EMRTemplate if hidden in CPP and hideDate <= item consultation date
        allMedicalProblemsByEMR = allMedicalProblemsByEMR.filterGroup(
            x => !x.some(mi =>
                mi.EMRTemplateIsVisibleInCPP == false
                && mi.HideDate != null
                && moment(mi.HideDate) <= moment(encounter.ConsultationDate)
            )
        );

        // ensure we display only medical items that existed at time of encounter (medicalitems.commitDate <= encounter.consultDate)
        // ensure we display medical items that have been added during this encounter (medicalitems.relatedEncounter = encounter.Id)
        let problemsByEmrTemplate = allMedicalProblemsByEMR.filter(x => {
            let creationDate = MedicalItemModel.getMedicalItemCreationDate(x);
            let isDisplayed = x.RelatedEncounterId == encounter.Id || (creationDate != null && moment(creationDate) <= moment(encounter.ConsultationDate));

            // INVALID FILTERING
            // when the medicalitem's EMRTemplate is marked as INVALID
            // we do not display the item if the latest invalidation date is smaller than the encounter consultation date
            // (we get the latest invalidation date from the validation notes)
            // when item is invalid and invalidation date >= consultation date, we still want to display the item but with an "invalid" style
            // also.. we do not display item in medical summary when the item has been added from this encounters' assessment section
            if (x.EMRTemplateIsInvalid) {
                let validationNotesForEMRTemplate = validationNotes[x.EMRTemplateId];
                if (validationNotesForEMRTemplate != null && validationNotesForEMRTemplate.length > 0) {
                    // get latest invalidation date
                    validationNotesForEMRTemplate = validationNotesForEMRTemplate.sortByPropertyDesc(x => x.SavedDate);
                    let latestInvalidationDate = validationNotesForEMRTemplate[0].SavedDate;

                    isDisplayed = isDisplayed && (latestInvalidationDate >= encounter.ConsultationDate);

                    x.DisplayAsInvalid = isDisplayed;
                }
            }

            if (x.RelatedEncounterId == encounter.Id && x.AnteriorityConsultationId == GuidHelper.Empty()) {
                // Is Assesment
                isDisplayed = isDisplayed && x.ForceDisplayInMedicalHistory;
            }

            return isDisplayed;
        });

        // set age at lifestage for sorting
        problemsByEmrTemplate.updateAllElement(x => x.LifeStageAge = PatientMedicalSummaryHelper.GetAgeAtLifeStageOnset(x, patientBirthDate));

        // Select only the lastest item of each group
        problemsByEmrTemplate.updateAll(problems => MedicalItemModel.sortByCreationDateDesc(problems));
        let lastProblemOfEachGroup = problemsByEmrTemplate.map(p => p.firstOrDefault());

        return lastProblemOfEachGroup.sortByPropertyDesc(x => x.LifeStageDate != null ? x.LifeStageDate : 0);
    }

    // used specifically to display medical problems in the medical history section of the encounter view
    public static GetMedicalProblemHistoryForNote(summaryModel: IPatientMedicalSummaryModel, noteModel: IPatientMedicalNoteModel, subType: MedicalItemSubtypeEnum = null, patientBirthDate: Date): MedicalItemProblemAndCondition[] {
        const encounter = noteModel.get("Encounter");
        return PatientMedicalSummaryHelper.GetMedicalProblemHistoryForEncounter(summaryModel, encounter, subType, patientBirthDate);
    }

    // used specifically to display medical problems in the assessment section of the encounter view
    public static GetMedicalProblemAssesmentForNote(allMedicalProblems: MedicalItemProblemAndCondition[], noteModel: IPatientMedicalNoteModel, patientBirthDate: Date): MedicalItemProblemAndCondition[] {
        const encounter = noteModel.get("Encounter");
        return PatientMedicalSummaryHelper.GetMedicalProblemAssesmentForEncounter(allMedicalProblems, encounter, patientBirthDate);
    }

    // used specifically to display medical problems in the assessment section of the encounter view
    public static GetMedicalProblemAssesmentForEncounter(allMedicalProblems: MedicalItemProblemAndCondition[], encounter: Encounter, patientBirthDate: Date): MedicalItemProblemAndCondition[] {
        let medicalItemsByEMRTemplate = toLookupDictionary(allMedicalProblems, x => x.EMRTemplateId);

        let validMedicalItemsByEmrTemplate = medicalItemsByEMRTemplate.filter(x => x.RelatedEncounterId == encounter.Id && x.AnteriorityConsultationId == GuidHelper.Empty());

        // set age at lifestage for sorting
        validMedicalItemsByEmrTemplate.updateAllElement(x => x.LifeStageAge = PatientMedicalSummaryHelper.GetAgeAtLifeStageOnset(x, patientBirthDate));
        validMedicalItemsByEmrTemplate.updateAll(items => MedicalItemModel.sortByCreationDateDesc(items));

        let problems = validMedicalItemsByEmrTemplate.map(x => x.firstOrDefault());

        return problems.sortByPropertyDesc(x => x.LifeStageDate != null ? x.LifeStageDate : 0);
    }

    // used specifically to display other medical items in the encounter view (ie risk factors, alerts, etc.)
    public static GetMedicalItemToDisplayForMedicalNote(allMedicalItems: MedicalItem[], summaryModel: IPatientMedicalSummaryModel, noteModel: IPatientMedicalNoteModel): MedicalItem[] {
        const encounter = noteModel.get("Encounter");
        return PatientMedicalSummaryHelper.GetMedicalItemToDisplayForMedicalEncounter(allMedicalItems, summaryModel, encounter);
    }

    // used specifically to display other medical items in the encounter view (ie risk factors, alerts, etc.)
    public static GetMedicalItemToDisplayForMedicalEncounter(allMedicalItems: MedicalItem[], summaryModel: IPatientMedicalSummaryModel, encounter: Encounter): MedicalItem[] {
        let validationNotes = summaryModel.get("EMRTemplateValidationNotes");

        let medicalItemsByEMRTemplate = toLookupDictionary(allMedicalItems, x => x.EMRTemplateId);

        // do not display EMRTemplate if hidden in CPP and hideDate <= item consultation date
        medicalItemsByEMRTemplate = medicalItemsByEMRTemplate.filterGroup(x =>
            !x.some(mi => mi.EMRTemplateIsVisibleInCPP == false && mi.HideDate != null && moment(mi.HideDate) <= moment(encounter.ConsultationDate))
        );

        return PatientMedicalSummaryHelper.prepareMedicalItemForDisplayInNote(medicalItemsByEMRTemplate, validationNotes, encounter);
    }

    public static prepareMedicalItemForDisplayInNote(medicalItemsByEMRTemplate: LookupDictionary<MedicalItem>, validationNotes: { [emrTemplateId: string]: ValidationNote[] }, encounter: Encounter): MedicalItem[] {
        // when multiple items in EMRTemplate, get the one that was valid at note signoff date, else the latest one
        let validMedicalItemsByEmrTemplate = medicalItemsByEMRTemplate.filter(x => {
            let creationDate = MedicalItemModel.getMedicalItemCreationDate(x);

            // do not display if medical item commitdate > encounter.consultationDate  unless it was added in the displayed encounter (item.EncounterId = encounter.Id)
            let isDisplayed = x.RelatedEncounterId == encounter.Id || (creationDate != null && moment(creationDate) <= moment(encounter.ConsultationDate));

            if (isDisplayed && x.EMRTemplateIsInvalid) { // Check if it was invalidated before or after the current note
                var validationNotesForEMRTemplate = validationNotes[x.EMRTemplateId]; // (we get the latest invalidation date from the validation notes)
                if (validationNotesForEMRTemplate != null && validationNotesForEMRTemplate.length > 0) {
                    // get latest invalidation date
                    validationNotesForEMRTemplate = validationNotesForEMRTemplate.sortByPropertyDesc(x => x.SavedDate);
                    var latestInvalidationDate = validationNotesForEMRTemplate[0].SavedDate;

                    isDisplayed = isDisplayed && (latestInvalidationDate >= encounter.ConsultationDate);

                    // when item is invalid and invalidation date >= consultation date, we still want to display the item but with an "invalid" style
                    x.DisplayAsInvalid = isDisplayed;
                }
            }

            return isDisplayed;
        });

        validMedicalItemsByEmrTemplate.updateAll(items => MedicalItemModel.sortByCreationDateDesc(items));

        return validMedicalItemsByEmrTemplate.map(x => x.firstOrDefault());
    }

    public static InitMedications(meds: MedicalItemMedicationAndPrescription[]): MedicationModel[] {
        var self = this;
        if (meds != null) {
            var items = meds.map(x => {
                x.PrescriptionTab_IsChecked = false;
                var NarcotiqueNonRenouvlable = MedicationLegalStatusHelper.isNarcotic(x.LegalStatus);

                x.Dosages = Immutable.List(x.Dosages); // Fix the immutable list from response automapping

                if (x.Dosages != null) {
                    x.Dosages = Immutable.List(
                        x.Dosages
                            .map(y => {
                                if (NarcotiqueNonRenouvlable)
                                    y.DisplayMode = MedicationDosageDisplayMode.Quantity;

                                if (y.DisplayMode != MedicationDosageDisplayMode.Quantity && y.Duration < 864000000000)
                                    y.Duration = 864000000000;

                                return y;
                            })
                            .toList()
                    );
                }
                return new MedicationModel(x);
            });

            var groups: { [key: string]: GroupedMedicationModel } = {};
            items.forEach(o => {
                var key = o.Model.Name;
                if (groups[key] == null) {
                    var newGroup = new GroupedMedicationModel();
                    newGroup.key = key;
                    newGroup.Items = [];
                    newGroup.MaxEndDate = null;

                    groups[key] = newGroup;
                }

                groups[key].Items.push(o);
                if (groups[key].MaxEndDate == null || groups[key].MaxEndDate < o.Model.RefillEndDate)
                    groups[key].MaxEndDate = o.Model.RefillEndDate;
            });

            var groupsArray = Object
                .keys(groups)
                .map(group => groups[group]);

            var SortedItems: MedicationModel[] = [];
            groupsArray = groupsArray
                .sort((curr: GroupedMedicationModel, next: GroupedMedicationModel) => self.SortByDate(curr.MaxEndDate, next.MaxEndDate));
            groupsArray.forEach(x => {
                x.Items = x.Items
                    .sort((curr: MedicationModel, next: MedicationModel) => self.SortByDate(curr.Model.RefillEndDate, next.Model.RefillEndDate));

                x.Items.forEach(y => {
                    SortedItems.push(y);
                });
            });

            items = SortedItems;

            return items;
        }
        else {
            return [];
        }
    }

    public static InitDiagnoses(problems: FavoriteDiagnosisComposite[]): DiagnosisModel[] {
        return problems?.map(p => new DiagnosisModel(p)) ?? [];
    }

    public static SortByDate(currentDate: Date, nextDate: Date, IsDescending: boolean = true): number {
        var result;

        if (currentDate == null && nextDate == null)
            result = 0;
        else if (currentDate == null)
            result = 1;
        else if (nextDate == null)
            result = -1;
        else if (moment(currentDate).isAfter(moment(nextDate))) {
            result = 1;
        }
        else if (moment(currentDate).isBefore(moment(nextDate)))
            result = -1;
        else
            result = 0;

        return IsDescending ? (-1 * result) : result;
    }

    public static CreatePrescriptionGroupsFromMedications(MedicationsToGroup: Immutable.List<MedicationModel>): PatientPrescriptionItem[] {
        var prescriptionIds = MedicationsToGroup
            .filter((y: MedicationModel) => y.Model.IsPast == false)
            .map((x: MedicationModel) => x.Model.EMRTemplatePrescriptionId).toArray();

        // Ceased Med Prescriptions Ids
        MedicationsToGroup.filter((y: MedicationModel) => y.Model.CeaseDate != null).forEach((x: MedicationModel) => {
            prescriptionIds.push(x.Model.CeasePrescriptionId);
        });

        var uniquePrescriptionIds = ArrayHelper.Unique(prescriptionIds).filter((x: string) => x != null && x != GuidHelper.Empty().toString());
        var prescriptions: PatientPrescriptionItem[] = [];

        uniquePrescriptionIds.forEach(currentPrescriptionId => {
            var medicationsRelated = MedicationsToGroup.filter((z: MedicationModel) => z.Model.EMRTemplatePrescriptionId == currentPrescriptionId || z.Model.CeasePrescriptionId == currentPrescriptionId).toArray();

            var ToSpliceIds: string[] = [];
            medicationsRelated.forEach(tmpMed => {
                if (tmpMed.Model.OriginalMedicationId != null) {
                    var relatedMeds = medicationsRelated.filter((relMed: MedicationModel) => relMed.Model.Id == tmpMed.Model.OriginalMedicationId);

                    if (relatedMeds != null && relatedMeds.length > 0) {
                        tmpMed.PrescriptionTab_OriginalMedication = relatedMeds[0];
                        ToSpliceIds.push(relatedMeds[0].Model.Id);
                    }
                }
            });

            var medicationsRelated = medicationsRelated.filter(tmpMed => !ToSpliceIds.some(y => tmpMed.Model.Id == y));

            var tmpPrescription = new PatientPrescriptionItem();
            tmpPrescription.Medications = medicationsRelated;
            const currentPrescriptionPrintHistory = medicationsRelated[0].Model.PrintHistory.filter(p => p.PrescriptionId === currentPrescriptionId);
            tmpPrescription.PrintHistory = lodash.orderBy(currentPrescriptionPrintHistory, p => p.PrintDate, "desc");

            var prescribedMedications = medicationsRelated.filter(q => q.Model.CeasePrescriptionId != currentPrescriptionId);
            if (prescribedMedications.length == 0) {
                // A prescription done that only has ceased medications in it that was also Created after Myle 2.2.1.74 (Integration of CeasePrescriptionId)
                tmpPrescription.HasOnlyCeasedMeds = true;

                var ceasedMed = medicationsRelated.filter(q => q.Model.CeasePrescriptionId == currentPrescriptionId);
                if (ceasedMed.length > 0) {
                    tmpPrescription.PrescriptionId = ceasedMed[0].Model.CeasePrescriptionId;
                    tmpPrescription.PrescriptionDate = ceasedMed[0].Model.CeaseDate;
                    tmpPrescription.EncounterId = ceasedMed[0].Model.CeaseEncounterId;

                    tmpPrescription.PrecribedByEmployeeId = ceasedMed[0].Model.CeaseEmployeeId;
                    if (tmpPrescription.PrecribedByEmployeeId == null)
                        tmpPrescription.PrecribedByEmployeeId = ceasedMed[0].Model.EmployeeId;

                    tmpPrescription.PrecribedByEmployee = cacheStore.GetEmployeeByContactFacetId(tmpPrescription.PrecribedByEmployeeId);
                }
            }
            else {
                tmpPrescription.isPending = prescribedMedications[0].Model.isPending;
                tmpPrescription.EncounterId = prescribedMedications[0].Model.RelatedEncounterId;
                tmpPrescription.PrescriptionDate = prescribedMedications[0].Model.PrescriptionDate;
                tmpPrescription.PrecribedByEmployeeId = prescribedMedications[0].Model.EmployeeId;
                tmpPrescription.PrecribedByEmployee = cacheStore.GetEmployeeByContactFacetId(tmpPrescription.PrecribedByEmployeeId);
                tmpPrescription.PrescriptionId = prescribedMedications[0].Model.EMRTemplatePrescriptionId;
            }

            if (tmpPrescription.PrescriptionDate != null) {
                prescriptions.forEach(p => {
                    p.Medications = p.Medications.sort((cur, next) => {
                        if (cur.Model.EMRTemplateSortIndex == next.Model.EMRTemplateSortIndex)
                            return 0;
                        else
                            return (cur.Model.EMRTemplateSortIndex > next.Model.EMRTemplateSortIndex) ? 1 : -1;
                    });
                });

                prescriptions.push(tmpPrescription);
            }
        });

        prescriptions = prescriptions.sort((cur, next) => {
            if (cur.isPending == next.isPending)
                return 0;
            else
                return (cur.isPending > next.isPending) ? -1 : 1;
        });

        return prescriptions;
    }

    public static setEMRTemplateVisibilityInCpp(listMedicalItems: MedicalItem[], emrTemplateId: string, hideDate: Date, isVisibleInCPP: boolean, isVisibleOnExport?: boolean): MedicalItem[] {
        if (listMedicalItems) {
            listMedicalItems.map((x: MedicalItem) => {
                if (x.EMRTemplateId == emrTemplateId) {
                    x.EMRTemplateIsVisibleInCPP = isVisibleInCPP;
                    x.EMRTemplateIsVisibleOnExport = isVisibleOnExport;
                    x.HideDate = hideDate;
                }
                return x;
            });
        }
        return listMedicalItems;
    }

    public static setEMRTemplateVisibilityOnExport(listMedicalItems: MedicalItem[], emrTemplateId: string, isVisibleOnExport: boolean): MedicalItem[] {
        if (listMedicalItems) {
            listMedicalItems.forEach((p: MedicalItem) => {
                if (p.EMRTemplateId === emrTemplateId) {
                    p.EMRTemplateIsVisibleOnExport = isVisibleOnExport;
                }
            });
        }
        return listMedicalItems;
    }

    public static setMedicalItemAlertsVisibilityOnExport(listOfAlerts: MedicalItemAlertsAndSpecialNeeds[], emrTemplateId: string, isVisibleOnExport: boolean): MedicalItemAlertsAndSpecialNeeds[] {
        if (listOfAlerts) {
            listOfAlerts.forEach((p: MedicalItemAlertsAndSpecialNeeds) => {
                if (p.EMRTemplateId === emrTemplateId) {
                    p.EMRTemplateIsVisibleOnExport = isVisibleOnExport;
                }
            });
        }
        return listOfAlerts;
    }

    public static getImmutableMedicalSummaryFromResponse(responseData: CumulativePatientProfileModel, patientModel: Patient): IPatientMedicalSummaryModel {
        // construct model and immutable model from response data
        let responseModel: IPatientMedicalSummaryModel = Immutable.fromJS(PatientModelHelper.getDefaultPatientSummaryModel());
        let responseModelData: IPatientMedicalSummaryModel = Immutable.fromJS(responseData);
        responseModel = responseModel.merge(responseModelData); // here we overwrite default values coming from helper with values coming from response

        // reset arrays in immutable model because Immutable.fromJS is too greedy and will transform the objects inside arrays
        // into immutable maps... we don't want that for ease of access in the views
        responseModel = responseModel.set("Encounters", Immutable.List(responseData.Encounters));
        responseModel = responseModel.set("DisplayedEncounters", Immutable.List(responseData.Encounters));

        if (patientModel)
            responseModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(responseModel, EncounterSearchIndexRebuildMode.SkipRebuild);

        responseModel = responseModel.set("EncountersSearchStrings", {});
        responseModel = responseModel.set("MedicalItemsAlertAndSpecialNeed", Immutable.List(<MedicalItemAlertsAndSpecialNeeds[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(responseData.MedicalItemsAlertAndSpecialNeed)));
        responseModel = responseModel.set("MedicalItemsFamilyHistory", Immutable.List(responseData.MedicalItemsFamilyHistory));
        responseModel = responseModel.set("MedicalItemsImmunization", Immutable.List(<MedicalItemImmunization[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(responseData.MedicalItemsImmunization)));
        responseModel = responseModel.set("MedicalItemsMedicalReaction", Immutable.List(responseData.MedicalItemsMedicalReaction));
        responseModel = responseModel.set("MedicalItemsScreeningTest", Immutable.List(responseData.MedicalItemsScreeningTest));
        responseModel = responseModel.set("FollowUps", Immutable.List(responseData.FollowUps));

        let medicationModels: MedicationModel[] = PatientMedicalSummaryHelper.InitMedications(responseData.MedicalItemsMedicationAndPrescription);
        responseModel = responseModel.set("MedicalItemsMedicationAndPrescription", Immutable.List(medicationModels));
        responseModel = responseModel.set("FilteredMedicalItemsMedicationAndPrescription", Immutable.List(medicationModels));

        const prescriptions = PatientMedicalSummaryHelper.CreatePrescriptionGroupsFromMedications(responseModel.get("MedicalItemsMedicationAndPrescription"));
        responseModel = responseModel.set("Prescriptions", Immutable.List(prescriptions));

        responseModel = responseModel.set("MedicalItemsRiskFactor", Immutable.List(<MedicalItemRiskFactor[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(responseData.MedicalItemsRiskFactor)));
        responseModel = responseModel.set("MedicalItemsVitalSign", Immutable.List(responseData.MedicalItemsVitalSign));
        // sort validation notes by EMRTemplates
        let filteredValidationNotes: { [emrTemplateId: string]: ValidationNote[] } = {};
        responseData.EMRTemplateValidationNotes.forEach(x => {
            if (Object.keys(filteredValidationNotes).indexOf(x.EMRTemplateId) < 0) {
                filteredValidationNotes[x.EMRTemplateId] = [];
            }

            filteredValidationNotes[x.EMRTemplateId].push(x);
        });
        responseModel = responseModel.set("EMRTemplateValidationNotes", filteredValidationNotes);

        // sort comments notes by EMRTemplates
        let filteredComments: { [emrTemplateId: string]: Comment[] } = {};
        responseData.EMRTemplateComments.forEach(comment => {
            if (Object.keys(filteredComments).indexOf(comment.EMRTemplateId) < 0) {
                filteredComments[comment.EMRTemplateId] = [];
            }

            filteredComments[comment.EMRTemplateId].push(comment);
        });
        responseModel = responseModel.set("EMRTemplateComments", filteredComments);

        // group medical problms by EMRTemplateId, when we have more than one item, choose the one with closest commit date
        // the medical item with closest date represent the latest evolution of a diagnostic, and it is upon this that we'll classify as ongoing/historical/etc.
        let medicalProblemsGroup = toLookupDictionary(responseData.MedicalItemsMedicalProblemAndCondition, x => x.EMRTemplateId);
        medicalProblemsGroup.updateAll(x => x.sortByPropertyDesc(x => x.CommitDate));

        // cycle through emrtemplateid groups, use first medical item (aka the most recent) and classify as ongoing/historica/surgery/etc.
        let resolvedStatusLookup = cacheStore.FindLookupByLookupCode("RESOLVED", "PROBLEMSTATUS");

        let ongoing = medicalProblemsGroup.filterGroup(x => {
            let firstItem = x[0];
            return firstItem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing ||
                (firstItem.EMRTemplateMedicalItemSubType == null && firstItem.ProblemStatusLookupId !== resolvedStatusLookup.LookupID);
        }).toArray();

        let historical = medicalProblemsGroup.filterGroup(x => {
            let firstItem = x[0];
            return firstItem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_Past ||
                (firstItem.EMRTemplateMedicalItemSubType == null && firstItem.ProblemStatusLookupId == resolvedStatusLookup.LookupID);
        }).toArray();

        let psychiatric = medicalProblemsGroup.filterGroup(x => {
            let firstItem = x[0];
            return firstItem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_Psychiatric;
        }).toArray();

        let surgery = medicalProblemsGroup.filterGroup(x => {
            let firstItem = x[0];
            return firstItem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast;
        }).toArray();

        let gynecologic = medicalProblemsGroup.filterGroup(x => {
            let firstItem = x[0];
            return firstItem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_Gynecology;
        }).toArray();

        // Force order by CommitDate,CreationDate on Ongoing problems, in coherence with order at creation and print steps
        responseModel = responseModel.set("MedicalItemsProblemsOnGoing", Immutable.List(<MedicalItemProblemAndCondition[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(ongoing)
            .sortByProperties(propertyComparer(x => x.CommitDate), propertyComparer(x => x.CreationDate))
        ));

        responseModel = responseModel.set("MedicalItemsProblemsHistory", Immutable.List(<MedicalItemProblemAndCondition[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(historical)));
        responseModel = responseModel.set("MedicalItemsSurgeryHistory", Immutable.List(<MedicalItemProblemAndCondition[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(surgery)));
        responseModel = responseModel.set("MedicalItemsPsychiatricHistory", Immutable.List(<MedicalItemProblemAndCondition[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(psychiatric)));
        responseModel = responseModel.set("MedicalItemsGynecologicalHistory", Immutable.List(<MedicalItemProblemAndCondition[]>PatientMedicalSummaryHelper.ReApplySortingAndNumbering(gynecologic)));

        responseModel = responseModel.set("ObstetricalSummary", responseData.ObstetricalSummary);

        return responseModel;
    }

    public static getImmutablePendingItemsFromResponse(responseData: PendingItemsModel, patientSummary: IPatientMedicalSummaryModel): IPatientMedicalSummaryModel {
        patientSummary = patientSummary
            .set("Requests", Immutable.List(responseData.Requests))
            .set("ConsultationRequests", Immutable.List(responseData.ConsultationRequests));

        return patientSummary;
    }

    public static getPatientAlertsToDisplay(alerts: MedicalItemAlertsAndSpecialNeeds[], patientModel: Patient): MedicalItemAlertsAndSpecialNeeds[] {
        let alertsToDisplay = alerts
            .groupBy(x => x.EMRTemplateId)
            .map(x => x.sortByProperty(y => y.CreationDate, true).firstOrDefault())
            .filter(x => {
                let isDisplay = x.EMRTemplateIsDisplayPopup && !x.EMRTemplateIsInvalid;

                if (isDisplay && x.LifeStageMode == LifeStageModeEnum.Date) {
                    if (isDisplay && x.EndDate != null && x.EndDate != undefined) {
                        isDisplay = moment() <= moment(x.EndDate) && moment() >= moment(x.LifeStageDate);
                    }
                    else {
                        isDisplay = moment() >= moment(x.LifeStageDate);
                    }
                }
                else if (isDisplay && x.LifeStageMode == LifeStageModeEnum.Age) {
                    if (patientModel.IndividualResponse.DateOfBirth != null) {
                        if (x.LifeStageAge) {
                            let minimumAgeInDays = 1000 * 365; // lets hope we don't get a vampire clinic

                            if (x.LifeStageAgeStatusLookupId == cacheStore.FindLookupByLookupCode("Day", "DayYear").LookupID)
                                minimumAgeInDays = x.LifeStageAge;
                            else if (x.LifeStageAgeStatusLookupId == cacheStore.FindLookupByLookupCode("Year", "DayYear").LookupID)
                                minimumAgeInDays = x.LifeStageAge * 365;

                            let ageInDays = moment().diff(moment(patientModel.IndividualResponse.DateOfBirth), "days");
                            isDisplay = ageInDays >= minimumAgeInDays;
                        }
                    }
                }
                else if (isDisplay && x.LifeStageMode == LifeStageModeEnum.LifeStage) {
                    let ageInDaysMin = 1000 * 365; // lets hope we don't get a vampire clinic
                    let ageInDaysMax = 1000 * 365; // lets hope we don't get a vampire clinic

                    if (patientModel.IndividualResponse.DateOfBirth != null) {
                        let ageInDays = moment().diff(moment(patientModel.IndividualResponse.DateOfBirth), "days");

                        if (x.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("NewBorn", "LIFESTAGE").LookupID) { // Newborn : Birth < 28 days
                            ageInDaysMin = 0;
                            ageInDaysMax = 28;
                        }
                        else if (x.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Infant", "LIFESTAGE").LookupID) { // Infant : 29 days < 2 years
                            ageInDaysMin = 29;
                            ageInDaysMax = 2 * 365 - 1;
                        }
                        else if (x.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Child", "LIFESTAGE").LookupID) { // Child : 2 years < 15 years
                            ageInDaysMin = 2 * 365;
                            ageInDaysMax = 16 * 365 - 1;
                        }
                        else if (x.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Adolescent", "LIFESTAGE").LookupID) { // Adolescent : 16 Years < 17 years
                            ageInDaysMin = 16 * 365;
                            ageInDaysMax = 18 * 365 - 1;
                        }
                        else if (x.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Adult", "LIFESTAGE").LookupID) { // Adult > 18 years
                            ageInDaysMin = 18 * 365;
                            ageInDaysMax = 1000 * 365; // lets hope we don't get a vampire clinic
                        }

                        isDisplay = ageInDays >= ageInDaysMin && ageInDays <= ageInDaysMax;
                    }
                }

                return isDisplay;
            });

        let alertsByEMRTemplate: { [emrTemplateId: string]: MedicalItemAlertsAndSpecialNeeds } = {};
        alertsToDisplay.forEach(x => {
            if (Object.keys(alertsByEMRTemplate).indexOf(x.EMRTemplateId) == -1) {
                alertsByEMRTemplate[x.EMRTemplateId] = x;
            }
            else {
                if (moment(x.ConsultationDate) > moment(alertsByEMRTemplate[x.EMRTemplateId].ConsultationDate)) {
                    alertsByEMRTemplate[x.EMRTemplateId] = x;
                }
            }
        });

        alertsToDisplay = [];
        Object.keys(alertsByEMRTemplate).forEach(k => {
            alertsToDisplay.push(alertsByEMRTemplate[k]);
        });

        return alertsToDisplay;
    }

    /**
     * Return patient summary model with the reset encountersSearchStrings
     * @param patientSummaryModel
     * @returns {IPatientMedicalSummaryModel}
     */
    public static makeEncountersSearchStringsReset(patientSummaryModel: IPatientMedicalSummaryModel): IPatientMedicalSummaryModel {
        patientSummaryModel = patientSummaryModel.set("EncountersSearchStrings", {});
        return patientSummaryModel;
    }

    /**
    * Set MedicalItem Previous Route in Patient Store
    * @param view
    * @param medicalItemSubtypeEnum
    * @returns {void}
    */
    public static setMedicalItemPreviousRoute(view: MedicalItemsTab, medicalItemSubtypeEnum: MedicalItemSubtypeEnum = null): void {
        switch (view) {
            case MedicalItemsTab.MedicalProblem: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem;
                if (medicalItemSubtypeEnum) {
                    patientStore.SelectedMedicalProblemSubType = medicalItemSubtypeEnum;
                }
                break;
            }
            case MedicalItemsTab.MedicalReactions: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewMedicalReactionItem;
                break;
            }
            case MedicalItemsTab.RiskFactors: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewRiskFactorItem;
                break;
            }
            case MedicalItemsTab.Alerts: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewAlertSpecialNeedItem;
                break;
            }
            case MedicalItemsTab.Immunization: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewImmunizationItem;
                break;
            }
            case MedicalItemsTab.FamilyHistory: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewFamilyHistoryItem;
                break;
            }
            case MedicalItemsTab.VitalSigns: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewVitalSignItem;
                break;
            }
            case MedicalItemsTab.ScreeningTest: {
                patientStore.MedicalItemPreviousRoute = LocalPatientOperations.MedicalNote.Edit_NewScreeningTestItem;
                break;
            }
        }
    }

    /**
     * Rebuild the search index for all encounters in the medical summary model
     * @param medicalSummaryModel The medical summary model to rebuild the search index for
     * @returns The medical summary model with the rebuilt search index
     */
    private static RebuildSearchIndex(medicalSummaryModel: IPatientMedicalSummaryModel): IPatientMedicalSummaryModel {
        const allEncounters = medicalSummaryModel.get("Encounters").toArray();
        if (!allEncounters?.length)
            return medicalSummaryModel;

        const newEncountersSearchStrings = MedicalNoteIndexHelper.buildEncounterIndexByMedicalSummaryModel(medicalSummaryModel);

        for (const encounter of allEncounters) {
            const encounterIndexByEncounter: string = MedicalNoteIndexHelper.buildEncounterIndexByEncounter(encounter) ?? "";
            const currentEncounterSearchString = newEncountersSearchStrings[encounter.Id] ?? "";

            newEncountersSearchStrings[encounter.Id] = currentEncounterSearchString.concat(" ", encounterIndexByEncounter);
        }

        return medicalSummaryModel.set("EncountersSearchStrings", newEncountersSearchStrings);
    }
}

export { EncounterSearchIndexRebuildMode, PatientMedicalSummaryHelper };
